import { useEffect, useState } from "react";
import { prettifyApiError } from "@/shared/api";
import { downloadDisputeFile, fetchDisputeCausali } from "../../api";

interface DisputeCausaDialogProps {
  orderRef: string;
  onClose: () => void;
  onDownloaded?: () => void;
}

/**
 * Scelta della CAUSA prima di scaricare il modulo "APERTURA PRATICA SDA".
 *
 * Prima il bottone "Contestazione" scaricava e basta, lasciando D6 (Tipo
 * Pratica) vuota: l'operatore doveva aprire Excel e scegliere dalla tendina.
 *
 * ⛔ Le causali NON sono scritte qui: arrivano da GET /orders/dispute/causali,
 * che le legge dal template. La cella ha data-validation A LISTA e un valore
 * fuori lista fa comparire "valore non valido" all'apertura del file — una
 * copia in TypeScript diverge alla prima revisione SDA, cioe' si rompe proprio
 * quando SDA cambia qualcosa.
 *
 * Stesso pattern modale di ``CallLogDialog``: backdrop, ESC per chiudere,
 * ``data-no-open`` cosi' il click non naviga altrove.
 */
export function DisputeCausaDialog({ orderRef, onClose, onDownloaded }: DisputeCausaDialogProps) {
  const [causali, setCausali] = useState<string[] | null>(null);
  const [causa, setCausa] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let annullato = false;
    fetchDisputeCausali()
      .then((o) => {
        if (!annullato) setCausali(o.causa);
      })
      .catch((e) => {
        // Se le causali non arrivano il dialog resta aperto e lo DICE: senza,
        // sarebbe una tendina vuota senza spiegazione.
        if (!annullato) setError(prettifyApiError(e));
      });
    return () => {
      annullato = true;
    };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  const canSubmit = causa !== "" && !busy;

  async function handleDownload() {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      await downloadDisputeFile(orderRef, causa);
      onDownloaded?.();
      onClose();
    } catch (err) {
      setError(prettifyApiError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-ink/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Apertura pratica SDA"
      data-no-open
      data-testid="dispute-causa-dialog"
      onClick={(e) => {
        e.stopPropagation();
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div className="w-full max-w-md rounded-lg border border-line bg-card p-5 shadow-popover">
        <h3 className="font-serif text-[16px] font-medium text-ink">Apertura pratica SDA</h3>
        <p className="mt-1 text-[12px] text-ink">
          Ordine <span className="font-mono text-ink">{orderRef}</span> — il modulo esce
          precompilato con LDV, contenuto, valore, mittente e destinatario.
        </p>

        <div className="mt-4 flex flex-col gap-3">
          <label className="flex flex-col gap-1 text-[12px] font-medium text-ink">
            Tipo pratica (causa del reclamo)
            <select
              value={causa}
              onChange={(e) => setCausa(e.target.value)}
              disabled={busy || causali === null}
              autoFocus
              aria-label="Tipo pratica"
              data-testid="dispute-causa-select"
              className="h-[44px] rounded-md border border-line bg-card px-3 text-[13px] font-normal text-ink outline-none focus:border-accent disabled:opacity-50"
            >
              <option value="" disabled>
                {causali === null ? "Carico le causali…" : "Seleziona…"}
              </option>
              {(causali ?? []).map((c) => (
                // key e value VERBATIM: una causale del modulo ha uno spazio
                // finale che fa parte del valore ammesso dalla tendina.
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
          <p className="text-[11.5px] text-ink">
            La nota descrittiva viene precompilata con ordine, LDV e destinatario: puoi completarla
            in Excel con il racconto dell&apos;anomalia.
          </p>
        </div>

        {error && (
          <p role="alert" className="mt-3 text-[12px] text-red">
            {error}
          </p>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-md border border-line bg-card px-3 py-2 text-[12px] font-medium text-ink hover:border-ink-4 disabled:opacity-50"
          >
            Annulla
          </button>
          <button
            type="button"
            disabled={!canSubmit}
            onClick={() => void handleDownload()}
            data-testid="dispute-download-btn"
            className="rounded-md border border-teal bg-teal px-3 py-2 text-[12px] font-medium text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? "Scarico…" : "Scarica modulo"}
          </button>
        </div>
      </div>
    </div>
  );
}
